import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import ProgressBar from '../../element/file/uploader/ProgressBar';

import useStalledBoolean from '../../utils/react/useStalledBoolean';
import { getFileSizeComparisonString } from '../../../common/files/fileSizeUtils';
import { triggerDownload } from '../../element/attachments/attachmentDownloadActions';
import { getAttachmentProgress } from '../../element/attachments/attachmentsSelector';
import { trackAttachmentDownload } from '../../element/attachments/attachmentsService';
import { useTranslator } from '../i18n/useTranslator';
import { userLocalePreferenceSelector } from '../../user/currentUserSelector';

import './DownloadButton.scss';

const DownloadButton = ({
    children,
    url,
    filename,
    onDownloadStart,
    onDownloadEnd,
    elementId,
    isShare,
    showDownloadProgress = true,
}) => {
    const commonTranslate = useTranslator('common');
    const [isDownloading, setIsDownloading] = useState(false);
    const dispatch = useDispatch();
    const { total, loaded } = useSelector((state) => getAttachmentProgress(state, { elementId }));
    const locale = useSelector(userLocalePreferenceSelector);
    const isDownloadingOverride = useStalledBoolean(isDownloading, 1500);
    const isDownloadInProgress = isDownloading || isDownloadingOverride;

    const onDownload = async (event) => {
        event && event.preventDefault();
        event && event.stopPropagation();
        if (isDownloadInProgress) return;

        trackAttachmentDownload({ url });

        dispatch(
            triggerDownload({
                url,
                filename,
                onDownloadStart: () => {
                    setIsDownloading(true);
                    onDownloadStart && onDownloadStart();
                },
                onDownloadEnd: () => {
                    setIsDownloading(false);
                    onDownloadEnd && onDownloadEnd();
                },
                id: elementId,
                isShare,
            }),
        );
    };

    const maxTotalRef = useRef(total);

    useEffect(() => {
        if (total > maxTotalRef.current) {
            maxTotalRef.current = total;
        }
    }, [total]);

    const visibleTotal = isDownloadingOverride && !isDownloading ? maxTotalRef.current : total;
    const visibleLoaded = isDownloadingOverride && !isDownloading ? maxTotalRef.current : loaded;

    return (
        <span className="DownloadButton">
            {showDownloadProgress && isDownloadInProgress ? (
                <div className="downloading">
                    <ProgressBar barbershop animate percentageComplete={(visibleLoaded / visibleTotal) * 100} />
                    <span className="progress-message">
                        {getFileSizeComparisonString(visibleLoaded, visibleTotal, true, commonTranslate, locale)}
                    </span>
                </div>
            ) : (
                <a className="download-button" onClick={onDownload} href={url}>
                    {children}
                </a>
            )}
        </span>
    );
};

DownloadButton.propTypes = {
    children: PropTypes.oneOfType([PropTypes.element, PropTypes.array, PropTypes.string]),
    url: PropTypes.string,
    filename: PropTypes.string,
    elementId: PropTypes.string,
    onDownloadStart: PropTypes.func,
    onDownloadEnd: PropTypes.func,
    isShare: PropTypes.bool,
    showDownloadProgress: PropTypes.bool,
};

export default DownloadButton;
